import { type DiscountCouponListItem, type DiscountCouponListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React, { useMemo } from 'react';

import { DiscountCouponFormDrawer } from '../components/DiscountCouponFormDrawer';
import { listDiscountCoupons } from '../discount-coupons.api';
import {
  useBulkDeleteDiscountCoupons,
  useBulkSetDiscountCouponsStatus,
  useDeleteDiscountCoupon,
  useDiscountCouponsList,
  useCurrencyOptions,
} from '../use-discount-coupons';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

const KIND_FILTER_OPTIONS = [
  { value: '', label: 'All kinds' },
  { value: 'PERCENT', label: 'Percent' },
  { value: 'FLAT', label: 'Flat' },
] as const;

const STATIC_FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'kind',
    label: 'Kind',
    options: KIND_FILTER_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-40',
  },
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

export const DiscountCouponsListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteCoupon = useDeleteDiscountCoupon();
  const bulkDeleteMut = useBulkDeleteDiscountCoupons();
  const bulkSetStatusMut = useBulkSetDiscountCouponsStatus();
  const currencyOptionsQuery = useCurrencyOptions();

  // Build a lookup map from currency uuid → code for rendering FLAT values.
  const currencyCodeMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of currencyOptionsQuery.data ?? []) {
      map.set(c.id, c.code);
    }
    return map;
  }, [currencyOptionsQuery.data]);

  const columns: ListScreenColumns<DiscountCouponListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<DiscountCouponListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Code / Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'kind',
        header: 'Kind',
        cell: (row) => (
          <span
            className={
              row.kind === 'PERCENT'
                ? 'inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700'
                : 'inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700'
            }
          >
            {row.kind === 'PERCENT' ? 'Percent' : 'Flat'}
          </span>
        ),
      },
      {
        id: 'value',
        header: 'Value',
        sortable: true,
        cell: (row) => {
          if (row.kind === 'PERCENT') {
            return <span>{row.value}%</span>;
          }
          const currencyCode = row.currencyId ? (currencyCodeMap.get(row.currencyId) ?? '') : '';
          return (
            <span>
              {currencyCode ? `${currencyCode} ` : ''}
              {row.value}
            </span>
          );
        },
      },
      {
        id: 'redemptions',
        header: 'Redemptions',
        cell: (row) => {
          const cap = row.maxRedemptions === 0 ? '∞' : String(row.maxRedemptions);
          return (
            <span className="text-sm text-zinc-600">
              {row.redemptionCount} / {cap}
            </span>
          );
        },
      },
      {
        id: 'validity',
        header: 'Validity',
        cell: (row) => {
          const from = row.validFrom ? new Date(row.validFrom).toLocaleDateString() : null;
          const until = row.validUntil ? new Date(row.validUntil).toLocaleDateString() : null;
          if (!from && !until) return <span className="text-zinc-400">-</span>;
          return (
            <span className="text-sm text-zinc-600">
              {from ?? '…'} - {until ?? '…'}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<DiscountCouponListItem, DiscountCouponListQuery>
      title="Coupons"
      description="Promo codes applied to orders (percent or flat), with redemption caps and a validity window."
      useList={useDiscountCouponsList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        const kind = state.filters.kind;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
          ...(kind === 'PERCENT' ? { kind: 'PERCENT' as const } : {}),
          ...(kind === 'FLAT' ? { kind: 'FLAT' as const } : {}),
        };
      }}
      columns={columns}
      filters={STATIC_FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add coupon' }}
      renderDrawer={({ open, mode, row, close }) => (
        <DiscountCouponFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          coupon={row}
        />
      )}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} coupon${rows.length === 1 ? '' : 's'}?`,
            description:
              'These coupons will be soft-deleted and will no longer be accepted at checkout. Existing orders that already applied them are unaffected.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} coupon${rows.length === 1 ? '' : 's'}?`,
            description: isActive
              ? 'They will be accepted at checkout again.'
              : 'They will be rejected at checkout but remain on historical orders.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listDiscountCoupons(q),
        filename: 'discount-coupons',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Kind', value: (r) => r.kind },
          { header: 'Value', value: (r) => r.value },
          { header: 'Max redemptions', value: (r) => r.maxRedemptions },
          { header: 'Redemption count', value: (r) => r.redemptionCount },
          { header: 'Valid from', value: (r) => r.validFrom ?? '' },
          { header: 'Valid until', value: (r) => r.validUntil ?? '' },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteCoupon.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this coupon?',
            description: `${row.name} will be soft-deleted and will no longer be accepted at checkout. Existing orders that already applied it are unaffected.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by name or code (min 3 characters)',
        emptyTitle: 'No coupons match these filters',
        emptyDescription: 'Adjust the kind / status filter or clear the search box.',
        errorTitle: 'Failed to load coupons',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
